import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import SignInProducer from './producer/signin-producer';
import SignInPresenter from './signin-presenter';
import CreateAccount from './create-account';
import Onboarding from './onboarding';
import ConfirmAppEntry from './confirm-app-entry';
import { ONBOARDING_ROUTES } from '../../stores/session-status';
import Socket from '../../connection/socket';
import WaitingIndicator from '../general-components/waiting-indicator';
import ForgotPassword from './forgot-password';
import PasswordReset from './password-reset';
import HomeContainer from './home/home-container';
import TellyChannelLogo from '../../images/app-store-icon.png';
import ProducerSelectApp from './producer/producer-select-app';
import { GreenroomState } from '../../stores/types/types';

interface Props {
	// props that will be passed to this component
	socket: Socket | null;
	connected: boolean;
}

const PRESENTER_ROUTES = [
	ONBOARDING_ROUTES.PRESENTER_LOGIN,
	ONBOARDING_ROUTES.PRESENTER_CREATE_ACCOUNT,
	ONBOARDING_ROUTES.PRESENTER_FORGOT_PASSWORD,
	ONBOARDING_ROUTES.PRESENTER_RESET_PASSWORD,
];

const PRODUCER_ROUTES = [
	ONBOARDING_ROUTES.PRODUCER_LOGIN,
	ONBOARDING_ROUTES.PRODUCER_FORGOT_PASSWORD,
	ONBOARDING_ROUTES.PRODUCER_RESET_PASSWORD,
	ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL,
	ONBOARDING_ROUTES.PRODUCER_SELECT_APP,
	ONBOARDING_ROUTES.PRODUCER_CREATE_ACCOUNT,
];

const NONE = [
	ONBOARDING_ROUTES.JOIN_PRESENTER,
	ONBOARDING_ROUTES.PRESENTER_SELECT_MEETING,
	ONBOARDING_ROUTES.PRODUCER_SELECT_MEETING,
	ONBOARDING_ROUTES.PRODUCER_CONFIRM,
];

const SHARED_ONBOARDING_HEADER = [
	ONBOARDING_ROUTES.PRESENTER_LOGIN,
	ONBOARDING_ROUTES.ONBOARDING,
	ONBOARDING_ROUTES.PRESENTER_CREATE_ACCOUNT,
	ONBOARDING_ROUTES.PRESENTER_FORGOT_PASSWORD,
	ONBOARDING_ROUTES.PRESENTER_RESET_PASSWORD,
	ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL,
	ONBOARDING_ROUTES.PRODUCER_LOGIN,
	ONBOARDING_ROUTES.PRODUCER_SELECT_APP,
	ONBOARDING_ROUTES.PRODUCER_CREATE_ACCOUNT,
	ONBOARDING_ROUTES.PRODUCER_FORGOT_PASSWORD,
	ONBOARDING_ROUTES.PRODUCER_RESET_PASSWORD,
];

export default function AppEntry(props: Props) {
	const {
		socket,
		connected,
	} = props;

	const onboardingRoute = useSelector(
		(state: GreenroomState) => state.SessionReducer.onboarding_route,
	);

	const dispatch = useDispatch();
	const onboardingTab = useSelector((state: GreenroomState) => state.SessionReducer.onboardingTab);
	const meetingModalOpen = useSelector(
		(state: GreenroomState) => state.SessionReducer.meetingModalOpen,
	);
	const useMobile = useSelector((state: GreenroomState) => state.VideoReducer.useMobile);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);

	const currentRoute = useMemo(() => {
		switch (onboardingRoute) {
			case ONBOARDING_ROUTES.JOIN_PRESENTER: {
				return (
					<div className='position-absolute align-items-center width-full height-full'>
						<WaitingIndicator />
					</div>
				);
			}

			case ONBOARDING_ROUTES.PRESENTER_LOGIN: {
				return (
					<SignInPresenter />
				);
			}

			case ONBOARDING_ROUTES.PRESENTER_CREATE_ACCOUNT:
			case ONBOARDING_ROUTES.PRODUCER_CREATE_ACCOUNT: {
				return (
					<CreateAccount />
				);
			}

			case ONBOARDING_ROUTES.ONBOARDING: {
				return (
					<Onboarding socket={socket} />
				);
			}

			case ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL:
			case ONBOARDING_ROUTES.PRODUCER_LOGIN: {
				return (
					<SignInProducer />
				);
			}

			case ONBOARDING_ROUTES.PRODUCER_SELECT_APP: {
				return (
					<ProducerSelectApp />
				);
			}

			case ONBOARDING_ROUTES.PRODUCER_SELECT_MEETING: {
				return <HomeContainer connected={connected} socket={socket} />;
			}

			case ONBOARDING_ROUTES.PRODUCER_CONFIRM: {
				return <ConfirmAppEntry />;
			}

			case ONBOARDING_ROUTES.PRESENTER_SELECT_MEETING: {
				return <HomeContainer connected={connected} socket={socket} />;
			}

			case ONBOARDING_ROUTES.PRESENTER_FORGOT_PASSWORD: {
				return <ForgotPassword />;
			}

			case ONBOARDING_ROUTES.PRESENTER_RESET_PASSWORD: {
				return <PasswordReset />;
			}

			case ONBOARDING_ROUTES.PRODUCER_FORGOT_PASSWORD: {
				return <ForgotPassword host={true} />;
			}

			case ONBOARDING_ROUTES.PRODUCER_RESET_PASSWORD: {
				return <PasswordReset host={true} />;
			}

			default: {
				return (
					<SignInPresenter />
				);
			}
		}
	}, [onboardingRoute, socket, connected, useMobile]);

	const onboardingHeader: JSX.Element = useMemo(() => {
		if (SHARED_ONBOARDING_HEADER.includes(onboardingRoute)) {
			return (
				<header className="entry-header">
					<div className="telly-channel-bubble-container">
						<div className="telly-channel-bubble" style={{ marginLeft: '24px' }}>
							<img src={TellyChannelLogo} alt='telly'></img>
						</div>
					</div>
				</header>
			);
		}

		return <></>;
	}, [onboardingRoute]);

	return useMemo(() => (
		<>
			<div className={classNames('app-entry', {
				presenter: PRESENTER_ROUTES.includes(onboardingRoute),
				producer: PRODUCER_ROUTES.includes(onboardingRoute),
				full: NONE.includes(onboardingRoute),
			})}>
				{onboardingHeader}
				<div style={{
					zIndex: 'auto',
					overflow: meetingModalOpen ? 'hidden' : '', // This gets rid of having 2 scrollbars (background and modal). When the modal is open we want one scrollbar visible for it.
				}}>
					{currentRoute}
				</div>
			</div>
		</>
	), [onboardingRoute, currentRoute,
		onboardingTab, meetingModalOpen, onboardingHeader]);
}
